import { useState } from 'react';
import { Button, Input, SidePanel } from '../../../ui';

interface TimeEntryRecordInput {
  id: string;
  task_name: string;
  start_time: string;
  end_time?: string | null;
  notes?: string | null;
}

interface TimeEntryEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  timeEntry: TimeEntryRecordInput;
  onSave: (updatedEntry: {
    id: string;
    task_name: string;
    start_time: string;
    end_time: string | null;
    notes: string;
  }) => void;
}

export default function TimeEntryEditModal({ isOpen, onClose, timeEntry, onSave }: TimeEntryEditModalProps) {
  const [editedEntry, setEditedEntry] = useState({
    id: timeEntry.id,
    task_name: timeEntry.task_name,
    start_time: new Date(timeEntry.start_time),
    end_time: timeEntry.end_time ? new Date(timeEntry.end_time) : null,
    notes: timeEntry.notes || '',
  });

  // Format date for datetime-local input
  const formatDatetimeLocal = (date: Date | null): string => {
    if (!date) return '';

    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  };

  const handleSubmit = () => {
    onSave({
      ...editedEntry,
      start_time: editedEntry.start_time.toISOString(),
      end_time: editedEntry.end_time ? editedEntry.end_time.toISOString() : null,
    });
  };

  return (
    <SidePanel
      open={isOpen}
      onClose={onClose}
      width={440}
      eyebrow="Time Clock"
      title="Edit Time Entry"
      footer={
        <>
          <Button icon="check" onClick={handleSubmit} disabled={!editedEntry.task_name.trim()}>
            Save Entry
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      <div className="ck-stagger" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <Input
          label="Task Name"
          value={editedEntry.task_name}
          onChange={(e) => setEditedEntry({ ...editedEntry, task_name: e.target.value })}
          placeholder="Task name..."
        />

        <div>
          <div className="t-eyebrow" style={{ marginBottom: '7px' }}>Start Time</div>
          <input
            type="datetime-local"
            value={formatDatetimeLocal(editedEntry.start_time)}
            onChange={(e) => {
              const newDate = new Date(e.target.value);
              if (!isNaN(newDate.getTime())) {
                setEditedEntry({ ...editedEntry, start_time: newDate });
              }
            }}
            className="ck-date-input"
          />
        </div>

        <div>
          <div className="t-eyebrow" style={{ marginBottom: '7px' }}>End Time</div>
          <input
            type="datetime-local"
            value={formatDatetimeLocal(editedEntry.end_time)}
            onChange={(e) => {
              if (e.target.value) {
                const newDate = new Date(e.target.value);
                if (!isNaN(newDate.getTime())) {
                  setEditedEntry({ ...editedEntry, end_time: newDate });
                }
              } else {
                setEditedEntry({ ...editedEntry, end_time: null });
              }
            }}
            className="ck-date-input"
          />
          {!editedEntry.end_time && (
            <p style={{ margin: '6px 0 0', fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
              Leave blank if still in progress.
            </p>
          )}
        </div>

        <div>
          <div className="t-eyebrow" style={{ marginBottom: '7px' }}>Notes</div>
          <textarea
            value={editedEntry.notes}
            onChange={(e) => setEditedEntry({ ...editedEntry, notes: e.target.value })}
            rows={3}
            placeholder="Additional notes..."
            className="ck-textarea"
          />
        </div>
      </div>
    </SidePanel>
  );
}
